/**
 * PromptAssistant — collapsible conversational helper that watches uploaded
 * images and produces a professional prompt for gpt-image-2 or Kling.
 *
 * Lives directly above the main prompt textarea in both Imagen and Video
 * modules. Collapsed: a single glass pill. Expanded: a 3-4 message mini-
 * chat with its own input at the bottom.
 *
 * Props
 *   endpoint: "image" | "video"
 *   contextPayload: object merged into each request body. Must contain
 *                   `image_storage_paths` (image mode) OR the video fields
 *                   (start_frame_path, end_frame_path, model,
 *                   duration_seconds). The component re-reads it each time
 *                   the user sends a message so newly-uploaded references
 *                   are included automatically.
 *   onUsePrompt(text): called when the manager clicks "Usar prompt".
 *   hasContext: boolean — if false, the send input is disabled with a hint
 *               like "Sube al menos una imagen…".
 *
 * The component parses the assistant reply looking for:
 *     ```prompt
 *     <final prompt>
 *     ```
 * When found, action buttons (Usar / Copiar / Ajustar / Otra versión)
 * render under the message. We never auto-send or auto-fill the main
 * prompt — the manager always confirms with "Usar prompt".
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import RecordingBar from '@/components/shared/RecordingBar';
import {
  Sparkles, ChevronDown, Loader2, Check, Copy, Wand2, RefreshCcw, Mic,
} from 'lucide-react';

// Rocket icon — same SVG used in the main chat send button to keep the
// send action visually consistent across Ruido.ai inputs.
const RocketIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
  </svg>
);

const API = process.env.REACT_APP_BACKEND_URL;

function xhrJson(method, url, body, timeout, extra) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    if (extra) Object.entries(extra).forEach(([k, v]) => v && xhr.setRequestHeader(k, v));
    xhr.timeout = timeout || 90000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { /* noop */ }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: { detail: 'Timeout' } });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

// Sanitize any error detail before showing it to the user. Backends or proxies
// occasionally return raw HTML (Cloudflare 502/503 pages) when upstream LLMs
// hiccup — we MUST never paint that into the UI.
function cleanError(detail, status) {
  const transient = status === 502 || status === 503 || status === 504 || status === 0;
  if (transient) {
    return 'OpenAI está temporalmente ocupado. Intenta de nuevo en unos segundos.';
  }
  let raw = '';
  if (typeof detail === 'string') raw = detail;
  else if (detail && typeof detail === 'object') raw = detail.message || '';
  if (!raw) return 'El asistente no pudo responder.';
  // Strip anything that smells like HTML / Cloudflare error page
  if (/<\!doctype|<html|<head|<body|cloudflare|cf-error/i.test(raw)) {
    return 'OpenAI respondió con un error temporal. Intenta de nuevo.';
  }
  // Hard cap so we never blast the UI with a wall of text.
  return raw.length > 240 ? `${raw.slice(0, 240)}…` : raw;
}
const PROMPT_FENCE = /```\s*prompt\s*\n([\s\S]*?)```/i;
function extractPrompt(text) {
  if (!text) return null;
  const m = text.match(PROMPT_FENCE);
  if (!m) return null;
  return m[1].trim();
}
// Strip the fenced block from the assistant message body so the chat
// display doesn't duplicate the final prompt (we render it as a card).
function stripFence(text) {
  if (!text) return '';
  return text.replace(PROMPT_FENCE, '').trim();
}

export default function PromptAssistant({
  endpoint,              // 'image' | 'video'
  contextPayload,        // object merged into request body
  onUsePrompt,
  hasContext = true,
  contextHint = 'Sube una imagen para empezar.',
  testIdPrefix = 'prompt-assist',
}) {
  const { session } = useAuth();
  const accessToken = session?.access_token || null;
  const authHeader = accessToken ? { Authorization: `Bearer ${accessToken}` } : null;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastFailedSend, setLastFailedSend] = useState(null); // text to retry
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [adjustingIdx, setAdjustingIdx] = useState(null);
  const [adjustText, setAdjustText] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Voice: transcribes speech directly into the assistant input (does NOT
  // auto-send — the manager still hits the rocket to ask the assistant).
  // Uses the exact same hook + <RecordingBar /> as the main chat.
  const voice = useVoiceRecorder({
    language: 'es',
    prompt: 'Brief para asistente de prompts de Ruido.ai (producción de imagen o video).',
    maxSeconds: 180,
    onTranscribed: (text) => {
      // Append to whatever the manager already typed — never replace.
      setInput(prev => (prev ? `${prev.trimEnd()} ${text}` : text));
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, 120) + 'px';
        }
      });
    },
  });

  // Surface the recorder's own notice (permission denied, timeout, no speech,
  // max time reached, etc.) as the main error banner so the user always gets
  // feedback. Translate the common browser-permission errors to clean Spanish.
  useEffect(() => {
    if (!voice.notice) return;
    const n = voice.notice.toLowerCase();
    if (
      n.includes('not allowed')      // Chrome/Safari NotAllowedError
      || n.includes('permission')    // Firefox wording
      || n.includes('denied')
      || n.includes('dismissed')
      || n.includes('access')
    ) {
      setError('Permite acceso al micrófono para usar transcripción por voz.');
    } else if (n.includes('not supported')) {
      setError('Este navegador no soporta grabación de voz.');
    } else if (n.includes('maximum recording')) {
      setError('Tiempo máximo de grabación alcanzado.');
    } else if (n.includes('no speech')) {
      setError('No se detectó voz. Intenta de nuevo.');
    } else {
      setError(voice.notice);
    }
    // Clear any lingering "retry" state — the failure wasn't a network one.
    setLastFailedSend(null);
  }, [voice.notice]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (userContent) => {
    if (!accessToken) { setError('Inicia sesión para usar el asistente.'); return; }
    if (!hasContext) { setError(contextHint); return; }
    const clean = (userContent || '').trim();
    if (!clean) return;
    setError('');
    const next = [...messages, { role: 'user', content: clean }];
    setMessages(next);
    setInput('');
    setLoading(true);
    const { ok, status, data } = await xhrJson(
      'POST',
      `${API}/api/prompt-assistant/${endpoint}`,
      { ...contextPayload, messages: next },
      90000,
      authHeader,
    );
    setLoading(false);
    if (!ok) {
      if (status === 401) {
        setError('Inicia sesión para usar el asistente.');
        setLastFailedSend(null);
      } else {
        setError(cleanError(data?.detail, status));
        // Track the last user message so we can offer a quick retry pill
        // when the upstream LLM is transiently unavailable (502/503/504).
        const transient = status === 502 || status === 503 || status === 504 || status === 0;
        setLastFailedSend(transient ? clean : null);
        // Roll back the optimistic user bubble so retry doesn't duplicate it.
        if (transient) setMessages(messages);
      }
      return;
    }
    setLastFailedSend(null);
    const msg = data?.message;
    if (msg?.content) {
      setMessages([...next, { role: 'assistant', content: msg.content }]);
    }
  };

  // "Otra versión": re-ask the assistant for a different take without
  // polluting the transcript with user chatter. We append a hidden nudge.
  const regenerate = async () => {
    if (!hasContext) { setError(contextHint); return; }
    setError('');
    const nudge = 'Dame otra versión del prompt, con un enfoque o estética distinta. Responde SOLO con el bloque ```prompt``` actualizado.';
    await send(nudge);
  };

  const applyAdjust = async (idx) => {
    const text = adjustText.trim();
    if (!text) return;
    setAdjustingIdx(null);
    setAdjustText('');
    await send(`Ajusta el prompt con este cambio: ${text}. Devuelve la versión actualizada en el bloque \`\`\`prompt\`\`\`.`);
  };

  const handleCopy = async (idx, text) => {
    try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1400);
  };

  const pillLabel = useMemo(() => 'Pídele a la IA que escriba el prompt', []);

  return (
    <div
      data-testid={`${testIdPrefix}-wrap`}
      className={`rounded-xl border transition-all ${
        open
          ? 'bg-white/60 border-gray-200/70 shadow-[0_2px_18px_rgba(0,0,0,0.04)]'
          : 'bg-white/40 border-gray-200/50 hover:border-gray-300/70'
      }`}
    >
      {/* Pill header */}
      <button
        type="button"
        data-testid={`${testIdPrefix}-toggle`}
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left"
      >
        <Sparkles className="w-4 h-4 text-emerald-500 shrink-0" />
        <span className="text-sm text-gray-700 font-medium">{pillLabel}</span>
        {messages.length > 0 && !open && (
          <span className="text-[11px] text-gray-400 ml-auto mr-2">{messages.length} mensaje{messages.length !== 1 ? 's' : ''}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-0 flex flex-col gap-2">
          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid={`${testIdPrefix}-messages`}
            className="rounded-lg bg-white/60 border border-gray-200/60 p-2 space-y-2 max-h-[280px] overflow-y-auto scrollbar-thin"
          >
            {messages.length === 0 && (
              <div className="text-[12px] text-gray-400 px-2 py-3">
                {hasContext
                  ? 'Estoy observando las imágenes del módulo. Cuéntame qué quieres lograr y te devuelvo un prompt profesional.'
                  : contextHint}
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                idx={i}
                message={m}
                onCopy={handleCopy}
                copiedIdx={copiedIdx}
                onUse={(text) => {
                  onUsePrompt?.(text);
                  setOpen(false);
                }}
                onAdjustStart={(ii) => { setAdjustingIdx(ii); setAdjustText(''); }}
                adjustingIdx={adjustingIdx}
                adjustText={adjustText}
                setAdjustText={setAdjustText}
                onAdjustApply={applyAdjust}
                onAdjustCancel={() => { setAdjustingIdx(null); setAdjustText(''); }}
                onRegenerate={regenerate}
                testIdPrefix={testIdPrefix}
              />
            ))}
            {loading && (
              <div className="flex items-center gap-2 px-2 py-1.5 text-[12px] text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Pensando…
              </div>
            )}
          </div>

          {error && (
            <div className="text-[11px] text-amber-600 px-1">{error}</div>
          )}

          {/* Input row — rocket send button + integrated mic */}
          {voice.isActive ? (
            <div className="rounded-lg bg-white/70 border border-gray-200/60 px-1.5 py-1">
              <RecordingBar
                testIdPrefix={`${testIdPrefix}-voice`}
                phase={voice.isUploading ? 'uploading' : 'recording'}
                levels={voice.levels}
                seconds={voice.seconds}
                maxSeconds={voice.maxSeconds}
                onConfirm={voice.confirm}
                onCancel={voice.cancel}
              />
            </div>
          ) : (
            <div className="flex items-end gap-1.5">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  data-testid={`${testIdPrefix}-input`}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    const el = e.target;
                    el.style.height = 'auto';
                    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send(input);
                    }
                  }}
                  placeholder={hasContext ? 'Explica qué quieres lograr…' : contextHint}
                  disabled={!hasContext && accessToken !== null ? false : !hasContext}
                  rows={1}
                  className="w-full px-3 py-2 pr-10 rounded-lg bg-white/70 border border-gray-200/60 text-sm text-gray-900 outline-none focus:border-gray-300 placeholder:text-gray-400 resize-none min-h-[40px] max-h-[120px]"
                />
                {/* Mic pinned inside the input, like the main chat */}
                <button
                  type="button"
                  onClick={voice.start}
                  disabled={voice.isRequesting}
                  data-testid={`${testIdPrefix}-mic-btn`}
                  title="Hablar (transcribir con IA)"
                  aria-label="Hablar"
                  className="absolute bottom-1.5 right-1.5 p-1.5 rounded-full text-gray-400 hover:text-gray-700 transition-colors active:scale-[0.95] disabled:opacity-50"
                >
                  <Mic className="w-[14px] h-[14px]" />
                </button>
              </div>
              {/* Rocket send — same icon as the main chat */}
              <button
                type="button"
                data-testid={`${testIdPrefix}-send`}
                onClick={() => send(input)}
                disabled={loading || !input.trim() || !hasContext}
                className={`shrink-0 p-2 rounded-full transition-all active:scale-[0.95] ${
                  loading || !input.trim() || !hasContext
                    ? 'text-gray-300 opacity-40 cursor-not-allowed'
                    : 'text-emerald-500 hover:text-emerald-600'
                }`}
                title="Enviar"
              >
                {loading
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <RocketIcon className="w-6 h-6" />}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Message bubble -------------------------------------------------------

function MessageBubble({
  idx, message, onCopy, copiedIdx, onUse,
  onAdjustStart, adjustingIdx, adjustText, setAdjustText, onAdjustApply, onAdjustCancel,
  onRegenerate, testIdPrefix,
}) {
  const isUser = message.role === 'user';
  const finalPrompt = !isUser ? extractPrompt(message.content) : null;
  const prose = !isUser && finalPrompt ? stripFence(message.content) : message.content;

  return (
    <div
      data-testid={`${testIdPrefix}-msg-${idx}`}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[85%] flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}>
        {prose && (
          <div
            className={`px-3 py-2 rounded-2xl text-[13px] leading-[1.45] whitespace-pre-wrap ${
              isUser
                ? 'bg-gray-900 text-white rounded-br-md'
                : 'bg-white text-gray-800 border border-gray-200/60 rounded-bl-md'
            }`}
          >
            {prose}
          </div>
        )}
        {finalPrompt && (
          <div
            data-testid={`${testIdPrefix}-final-prompt-${idx}`}
            className="w-full rounded-xl border border-emerald-200 bg-emerald-50/50 p-2.5 flex flex-col gap-2"
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-700">
              <Sparkles className="w-3 h-3" /> Prompt propuesto
            </div>
            <div className="text-[13px] text-gray-800 leading-[1.45] whitespace-pre-wrap">
              {finalPrompt}
            </div>
            {adjustingIdx === idx ? (
              <div className="flex items-center gap-2 pt-1">
                <input
                  autoFocus
                  data-testid={`${testIdPrefix}-adjust-input-${idx}`}
                  value={adjustText}
                  onChange={(e) => setAdjustText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onAdjustApply(idx);
                    if (e.key === 'Escape') onAdjustCancel();
                  }}
                  placeholder="Cambia X por Y, más cinematográfico, etc."
                  className="flex-1 px-2.5 py-1.5 rounded-md bg-white border border-gray-200 text-[12px] outline-none focus:border-gray-300"
                />
                <button
                  onClick={() => onAdjustApply(idx)}
                  className="px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-[11px] hover:bg-gray-800"
                >
                  Aplicar
                </button>
                <button
                  onClick={onAdjustCancel}
                  className="px-2 py-1.5 rounded-md text-[11px] text-gray-500 hover:text-gray-700"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 flex-wrap pt-1">
                <ActionBtn
                  testId={`${testIdPrefix}-use-${idx}`}
                  primary
                  onClick={() => onUse(finalPrompt)}
                  icon={<Check className="w-3 h-3" />}
                  label="Usar prompt"
                />
                <ActionBtn
                  testId={`${testIdPrefix}-copy-${idx}`}
                  onClick={() => onCopy(idx, finalPrompt)}
                  icon={copiedIdx === idx ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  label={copiedIdx === idx ? 'Copiado' : 'Copiar'}
                />
                <ActionBtn
                  testId={`${testIdPrefix}-adjust-${idx}`}
                  onClick={() => onAdjustStart(idx)}
                  icon={<Wand2 className="w-3 h-3" />}
                  label="Ajustar"
                />
                <ActionBtn
                  testId={`${testIdPrefix}-regen-${idx}`}
                  onClick={onRegenerate}
                  icon={<RefreshCcw className="w-3 h-3" />}
                  label="Otra versión"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon, label, primary, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all active:scale-[0.97] ${
        primary
          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
          : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
